import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { logError } from "@/lib/log";
import { Plus, Trash2, FileDown } from "lucide-react";
import { formatDate, formatINR } from "@/lib/format";
import { downloadFile } from "@/lib/download";
import { TrialBalanceTable } from "@/components/journal/TrialBalanceTable";
import { JournalEntryForm } from "@/components/journal/JournalEntryForm";
import { LedgerDrilldown } from "@/components/journal/LedgerDrilldown";

const RANGES = [
  { key: "month", label: "This month" },
  { key: "3m", label: "3 months" },
  { key: "6m", label: "6 months" },
  { key: "ytd", label: "Year to date" },
];

export default function Journal() {
  const [range, setRange] = useState("ytd");
  const [tb, setTb] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [entries, setEntries] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [drillId, setDrillId] = useState(null);

  const load = useCallback(async () => {
    try {
      const [tbRes, acc, je] = await Promise.all([
        api.get(`/ledger/trial-balance?range=${range}`),
        api.get("/ledger/accounts"),
        api.get("/ledger/journal"),
      ]);
      setTb(tbRes.data);
      setAccounts(acc.data);
      setEntries(je.data);
    } catch (e) {
      logError("Journal load failed", e);
      toast.error("Failed to load ledger");
    }
  }, [range]);
  useEffect(() => { load(); }, [load]);

  const acctName = (id) => {
    const a = accounts.find((x) => x.id === id);
    return a ? `${a.code} · ${a.name}` : id;
  };

  const removeEntry = async (id) => {
    await api.delete(`/ledger/journal/${id}`);
    toast.success("Entry deleted");
    load();
  };

  return (
    <div className="space-y-8">
      <div className="flex items-end justify-between border-b border-[#1A1F16] pb-6 flex-wrap gap-4">
        <div>
          <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#6C7565]">Section VIII</span>
          <h2 className="font-serif text-5xl tracking-tight mt-2">Journal.</h2>
          <p className="text-sm text-[#6C7565] mt-2 max-w-lg">Double-entry bookkeeping. Your everyday transactions are auto-posted here; add manual entries for capital, transfers, adjustments and more.</p>
        </div>
        <button
          data-testid="add-journal-entry-btn"
          onClick={() => setShowForm(true)}
          className="bg-[#1A1F16] text-[#F7F5F0] px-5 py-3 text-xs uppercase tracking-[0.25em] hover:bg-[#2C3628] flex items-center gap-2"
        >
          <Plus className="w-3.5 h-3.5" /> New entry
        </button>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex gap-2 flex-wrap" data-testid="journal-range">
          {RANGES.map((r) => (
            <button
              key={r.key}
              data-testid={`jrange-${r.key}`}
              onClick={() => setRange(r.key)}
              className={`px-4 py-2 text-xs uppercase tracking-[0.15em] border transition-colors ${
                range === r.key ? "bg-[#1A1F16] text-[#F7F5F0] border-[#1A1F16]" : "border-[#D8D4C7] text-[#6C7565] hover:border-[#1A1F16]"
              }`}
            >{r.label}</button>
          ))}
        </div>
        <div className="flex gap-2">
          <button
            data-testid="export-tally-btn"
            onClick={() => exportCsv("tally")}
            className="border border-[#1A1F16] px-4 py-2 text-xs uppercase tracking-[0.15em] hover:bg-[#1A1F16] hover:text-[#F7F5F0] flex items-center gap-2"
          >
            <FileDown className="w-3.5 h-3.5" /> Tally CSV
          </button>
          <button
            data-testid="export-zoho-btn"
            onClick={() => exportCsv("zoho")}
            className="border border-[#1A1F16] px-4 py-2 text-xs uppercase tracking-[0.15em] hover:bg-[#1A1F16] hover:text-[#F7F5F0] flex items-center gap-2"
          >
            <FileDown className="w-3.5 h-3.5" /> Zoho CSV
          </button>
        </div>
      </div>

      <TrialBalanceTable data={tb} onSelect={(r) => setDrillId(r.id)} />

      {/* Manual journal entries list */}
      <section className="border border-[#D8D4C7] bg-white" data-testid="manual-entries">
        <div className="border-b border-[#1A1F16] px-6 py-4">
          <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#6C7565]">Manual entries</div>
          <h3 className="font-serif text-2xl tracking-tight">Journal book.</h3>
        </div>
        {entries.length === 0 ? (
          <p className="px-6 py-8 text-center text-sm text-[#6C7565]">No manual entries yet. Your transactions are still auto-posted into the trial balance above.</p>
        ) : (
          <div className="divide-y divide-[#EAE7DD]">
            {entries.map((je) => (
              <div key={je.id} className="px-6 py-4" data-testid={`journal-entry-${je.id}`}>
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <span className="num text-sm text-[#6C7565]">{formatDate(je.date)}</span>
                    <span className="ml-3 text-sm">{je.narration || <span className="italic text-[#6C7565]">No narration</span>}</span>
                  </div>
                  <button onClick={() => removeEntry(je.id)} data-testid={`delete-journal-${je.id}`} className="text-[#6C7565] hover:text-[#D94F3D]"><Trash2 className="w-4 h-4" /></button>
                </div>
                <div className="pl-4 space-y-0.5">
                  {je.lines.map((l, i) => (
                    <div key={`${je.id}-line-${i}`} className="grid grid-cols-12 text-sm text-[#4A5142]">
                      <span className="col-span-6">{acctName(l.account_id)}</span>
                      <span className="col-span-3 text-right num">{l.debit ? formatINR(l.debit) : ""}</span>
                      <span className="col-span-3 text-right num text-[#6C7565]">{l.credit ? formatINR(l.credit) : ""}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {showForm && (
        <JournalEntryForm accounts={accounts} onClose={() => setShowForm(false)} onSaved={load} />
      )}
      {drillId && (
        <LedgerDrilldown accountId={drillId} range={range} onClose={() => setDrillId(null)} />
      )}
    </div>
  );
}
